import { useState } from "react";
import { motion } from "framer-motion";
import {
  Mail,
  MapPin,
  Clock,
  Send,
  User,
  Building2,
  Briefcase,
  Globe,
  MessageSquare,
  CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { AnimatedSection, FadeIn } from "@/components/AnimatedSection";
import axios from "axios";
import SEO from "@/components/SEO";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
const API = `${BACKEND_URL}/api`;

const contactInfo = [
  {
    icon: Mail,
    title: "Email",
    value: "info@vanco.ai",
    link: "mailto:info@vanco.ai",
  },
  {
    icon: MapPin,
    title: "USA Office · KIDLIN Labs Inc.",
    value: "2604 Whittier Place, Wilmington, DE 19808, USA",
    link: null,
  },
  {
    icon: MapPin,
    title: "India Office · CODEBUGGED Organization Pvt Ltd",
    value: "4th Floor, Block A, BHIVE Premium HSR Campus, AKR Tech Park, 7th Mile, Hosur Road, Bengaluru, Karnataka 560068, India",
    link: "https://maps.app.goo.gl/1p26zC9Hvp7j4qpYA",
  },
  {
    icon: Clock,
    title: "Operating Hours",
    value: "Mon-Fri: 8 AM - 8 PM",
    link: null,
  },
];

const queryTypes = [
  { value: "sales", label: "Sales Inquiry" },
  { value: "partnership", label: "Partnership" },
  { value: "support", label: "Support" },
];

export default function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    country: "",
    organization: "",
    job_title: "",
    query_type: "",
    message: "",
    website: "", // Honeypot field for bot detection
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, query_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check for recent submissions (Frontend Throttling - 10-minute cooldown)
    const lastSubmission = localStorage.getItem("last_contact_submission");
    const COOLDOWN_PERIOD = 10 * 60 * 1000; // 10 minutes in ms

    if (lastSubmission) {
      const timeSinceLast = Date.now() - parseInt(lastSubmission);
      if (timeSinceLast < COOLDOWN_PERIOD) {
        const minutesLeft = Math.ceil((COOLDOWN_PERIOD - timeSinceLast) / 60000);
        toast.error(`You've sent a message recently. Please wait ${minutesLeft} minute(s) before sending another.`);
        return;
      }
    }

    if (!formData.full_name || !formData.email || !formData.query_type || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);

      // Save submission time to localStorage
      localStorage.setItem("last_contact_submission", Date.now().toString());

      setIsSubmitted(true);
      toast.success("Thank you! We'll be in touch soon.");
      setFormData({
        full_name: "",
        email: "",
        phone: "",
        country: "",
        organization: "",
        job_title: "",
        query_type: "",
        message: "",
        website: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      if (error.response?.status === 429) {
        toast.error("Too many submissions. Please try again after an hour.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="overflow-hidden" data-testid="contact-page">
      {/* Hero Section */}
      <SEO
        title={"Contact VANCO | Schedule an AI Strategy Consultation"}
        description={"Talk to VANCO about custom AI, automation and full-stack engineering. Book a free AI strategy consultation with our team in Bengaluru, India."}
        path="/contact"
      />
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-white via-slate-50 to-blue-50" data-testid="contact-hero">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#243b7f]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-[#f7c550]/10 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-2 bg-[#243b7f]/10 text-[#243b7f] rounded-full text-sm font-medium mb-6">
              Contact Us
            </span>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-[#0f172a] font-['Manrope'] mb-6">
              Ready to build AI that{" "}
              <span className="text-[#243b7f]">works?</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Let's talk about how we can help transform your business with intelligent solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="relative py-24 md:py-32 bg-gradient-to-br from-white via-slate-50 to-blue-50" data-testid="contact-form-section">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-0 w-[400px] h-[400px] bg-[#243b7f]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-0 w-[300px] h-[300px] bg-[#f7c550]/10 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <FadeIn>
                <h2 className="text-2xl font-semibold text-[#0f172a] font-['Manrope'] mb-8">
                  Get in Touch
                </h2>
                <div className="space-y-6">
                  {contactInfo.map((info) => (
                    <div key={info.title} className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#243b7f]/10 flex items-center justify-center flex-shrink-0">
                        <info.icon className="w-6 h-6 text-[#243b7f]" />
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900 mb-1">{info.title}</h4>
                        {info.link ? (
                          <a
                            href={info.link}
                            className="text-slate-600 hover:text-[#243b7f] transition-colors"
                            data-testid={`contact-${info.title.toLowerCase()}`}
                          >
                            {info.value}
                          </a>
                        ) : (
                          <p className="text-slate-600">{info.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </FadeIn>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <AnimatedSection>
                <Card className="border-slate-100 shadow-xl shadow-slate-200/50">
                  <CardContent className="p-8">
                    {isSubmitted ? (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center py-12"
                      >
                        <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                          <CheckCircle className="w-10 h-10 text-emerald-600" />
                        </div>
                        <h3 className="text-2xl font-semibold text-[#0f172a] font-['Manrope'] mb-3">
                          Thank you!
                        </h3>
                        <p className="text-slate-600 mb-6">
                          We've received your message and will get back to you within 24 hours.
                        </p>
                        <Button
                          onClick={() => setIsSubmitted(false)}
                          variant="outline"
                          className="border-[#243b7f]/30 text-[#243b7f]"
                          data-testid="send-another-message"
                        >
                          Send Another Message
                        </Button>
                      </motion.div>
                    ) : (
                      <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Full Name */}
                          <div className="space-y-2">
                            <Label htmlFor="full_name" className="text-slate-700">
                              Full Name <span className="text-red-500">*</span>
                            </Label>
                            <div className="relative">
                              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                              <Input
                                id="full_name"
                                name="full_name"
                                value={formData.full_name}
                                onChange={handleChange}
                                placeholder="John Doe"
                                className="pl-10 h-12 border-slate-200 focus:border-[#243b7f] focus:ring-[#243b7f]/20"
                                required
                                data-testid="input-full-name"
                              />
                            </div>
                          </div>

                          {/* Email */}
                          <div className="space-y-2">
                            <Label htmlFor="email" className="text-slate-700">
                              Business Email <span className="text-red-500">*</span>
                            </Label>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                              <Input
                                id="email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="john@company.com"
                                className="pl-10 h-12 border-slate-200 focus:border-[#243b7f] focus:ring-[#243b7f]/20"
                                required
                                data-testid="input-email"
                              />
                            </div>
                          </div>



                          {/* Country */}
                          <div className="space-y-2">
                            <Label htmlFor="country" className="text-slate-700">
                              Country
                            </Label>
                            <div className="relative">
                              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                              <Input
                                id="country"
                                name="country"
                                value={formData.country}
                                onChange={handleChange}
                                placeholder="United States"
                                className="pl-10 h-12 border-slate-200 focus:border-[#243b7f] focus:ring-[#243b7f]/20"
                                data-testid="input-country"
                              />
                            </div>
                          </div>

                          {/* Organization */}
                          <div className="space-y-2">
                            <Label htmlFor="organization" className="text-slate-700">
                              Organization
                            </Label>
                            <div className="relative">
                              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                              <Input
                                id="organization"
                                name="organization"
                                value={formData.organization}
                                onChange={handleChange}
                                placeholder="Company Inc."
                                className="pl-10 h-12 border-slate-200 focus:border-[#243b7f] focus:ring-[#243b7f]/20"
                                data-testid="input-organization"
                              />
                            </div>
                          </div>

                          {/* Job Title */}
                          <div className="space-y-2">
                            <Label htmlFor="job_title" className="text-slate-700">
                              Job Title
                            </Label>
                            <div className="relative">
                              <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                              <Input
                                id="job_title"
                                name="job_title"
                                value={formData.job_title}
                                onChange={handleChange}
                                placeholder="CTO"
                                className="pl-10 h-12 border-slate-200 focus:border-[#243b7f] focus:ring-[#243b7f]/20"
                                data-testid="input-job-title"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Query Type */}
                        <div className="space-y-2">
                          <Label htmlFor="query_type" className="text-slate-700">
                            Query Type <span className="text-red-500">*</span>
                          </Label>
                          <Select
                            value={formData.query_type}
                            onValueChange={handleSelectChange}
                            required
                          >
                            <SelectTrigger
                              className="h-12 border-slate-200 focus:border-[#243b7f] focus:ring-[#243b7f]/20"
                              data-testid="select-query-type"
                            >
                              <SelectValue placeholder="Select query type" />
                            </SelectTrigger>
                            <SelectContent>
                              {queryTypes.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Message */}
                        <div className="space-y-2">
                          <Label htmlFor="message" className="text-slate-700">
                            Message <span className="text-red-500">*</span>
                          </Label>
                          <div className="relative">
                            <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                            <Textarea
                              id="message"
                              name="message"
                              value={formData.message}
                              onChange={handleChange}
                              placeholder="Tell us about your project or inquiry..."
                              className="pl-10 min-h-[150px] border-slate-200 focus:border-[#243b7f] focus:ring-[#243b7f]/20"
                              required
                              data-testid="input-message"
                            />
                          </div>
                        </div>

                        {/* Honeypot field - hidden from users but visible to bots */}
                        <div style={{ display: "none" }} aria-hidden="true">
                          <input
                            type="text"
                            name="website"
                            value={formData.website}
                            onChange={handleChange}
                            tabIndex="-1"
                            autoComplete="off"
                          />
                        </div>

                        {/* Submit Button */}
                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full bg-[#243b7f] hover:bg-[#1e3a8a] text-white rounded-full h-14 text-lg font-medium transition-all duration-300 hover:shadow-lg hover:shadow-blue-900/20"
                          data-testid="submit-contact-form"
                        >
                          {isSubmitting ? (
                            <span className="flex items-center gap-2">
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                              />
                              Sending...
                            </span>
                          ) : (
                            <span className="flex items-center gap-2">
                              <Send className="w-5 h-5" />
                              Send Message
                            </span>
                          )}
                        </Button>
                      </form>
                    )}
                  </CardContent>
                </Card>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
